/**
 * SmartKick — Face ID / Touch ID (biometric) login service.
 *
 * Wraps the native `capacitor-native-biometric` plugin. All calls are guarded
 * by a native-platform + plugin-availability check, so on the web build every
 * function is a safe no-op and the normal email/password login is unchanged.
 *
 * Design: we store the user's email + password in the device Keychain (iOS) /
 * Keystore (Android), gated behind a biometric prompt. On app launch we verify
 * identity with Face ID and re-authenticate against the existing `/auth/login`
 * endpoint — so no backend changes are needed and the session never relies on a
 * stale 24h JWT.
 */

const BIOMETRIC_SERVER = 'co.uk.smartkick.app';
export const BIOMETRIC_ENABLED_KEY = 'sk_biometric_enabled_v1';

// ----- Environment detection -----------------------------------------------
const isNative = () => {
  if (typeof window === 'undefined') return false;
  const cap = window.Capacitor;
  return Boolean(cap && typeof cap.isNativePlatform === 'function' && cap.isNativePlatform());
};

/** True only on iOS/Android native builds where the plugin can run. */
export const isBiometricEnvironment = () => isNative();

/** True when the user has previously opted in to biometric login on this device. */
export const isBiometricEnabled = () => {
  if (!isBiometricEnvironment()) return false;
  try {
    return localStorage.getItem(BIOMETRIC_ENABLED_KEY) === '1';
  } catch {
    return false;
  }
};

// Lazy-load the native plugin so the web bundle never tries to resolve it.
const loadPlugin = async () => {
  const mod = await import('capacitor-native-biometric');
  return mod.NativeBiometric;
};

/**
 * Returns { available, biometryType } describing whether the device has
 * usable biometric hardware that is enrolled. Errors degrade to unavailable.
 */
export const checkBiometricAvailability = async () => {
  if (!isBiometricEnvironment()) return { available: false };
  try {
    const NativeBiometric = await loadPlugin();
    const res = await NativeBiometric.isAvailable();
    return { available: Boolean(res?.isAvailable), biometryType: res?.biometryType };
  } catch {
    return { available: false };
  }
};

/**
 * Prompt Face ID/Touch ID and, on success, persist the credentials securely.
 * Throws if biometrics are unavailable or the user cancels/fails the prompt.
 */
export const enableBiometricLogin = async ({ email, password }) => {
  if (!isBiometricEnvironment()) return;
  const NativeBiometric = await loadPlugin();

  const { available } = await checkBiometricAvailability();
  if (!available) {
    throw new Error('Biometric login is not available on this device.');
  }

  await NativeBiometric.verifyIdentity({
    reason: 'Enable quick sign in',
    title: 'Enable Face ID',
    subtitle: 'SmartKick',
    description: 'Use Face ID to sign in faster next time.',
  });

  await NativeBiometric.setCredentials({
    username: email,
    password,
    server: BIOMETRIC_SERVER,
  });

  try {
    localStorage.setItem(BIOMETRIC_ENABLED_KEY, '1');
  } catch {
    /* ignore storage errors */
  }
};

/** Remove stored credentials + opt-in flag (called on logout / disable). */
export const disableBiometricLogin = async () => {
  try {
    localStorage.removeItem(BIOMETRIC_ENABLED_KEY);
  } catch {
    /* ignore */
  }
  if (!isBiometricEnvironment()) return;
  try {
    const NativeBiometric = await loadPlugin();
    await NativeBiometric.deleteCredentials({ server: BIOMETRIC_SERVER });
  } catch {
    /* ignore — best effort */
  }
};

/**
 * Prompt Face ID and, on success, return the stored credentials so the caller
 * can re-authenticate. Returns null on cancel/failure (→ fall back to login).
 */
export const getBiometricCredentials = async () => {
  if (!isBiometricEnvironment()) return null;
  try {
    const NativeBiometric = await loadPlugin();

    const { available } = await checkBiometricAvailability();
    if (!available) return null;

    await NativeBiometric.verifyIdentity({
      reason: 'Sign in to SmartKick',
      title: 'Sign in',
      subtitle: 'SmartKick',
      description: 'Use Face ID to sign in.',
    });

    const creds = await NativeBiometric.getCredentials({ server: BIOMETRIC_SERVER });
    if (!creds?.username || !creds?.password) return null;
    return { email: creds.username, password: creds.password };
  } catch {
    return null;
  }
};
